import { SettingsProvider } from '@/components/settings/SettingsProvider'
import { RouteSettingsProviders } from '@/components/settings/RouteSettingsProviders'
import { SettingsShell } from '@/components/settings/shell/SettingsShell'
import {
    canAccessSettingsRoute,
    getSettingsAccessDeniedRedirect,
    getSettingsProviderFlags,
    settingsRouteRequiresClient,
} from '@/lib/settings/settingsAccess'
import { AuthContext } from '@/modules/auth/authContext'
import { useContext, useEffect } from 'react'
import { useRouter } from 'next/router'

type SettingsLayoutProps = {
    pathname: string
    children: React.ReactNode
}

export const SettingsLayout = ({ pathname, children }: SettingsLayoutProps) => {
    const router = useRouter()
    const { isSuperUser, client } = useContext(AuthContext)
    const isOrganicClient = client?.leadSource === 'ORGANIC'
    const access = { isSuperUser, isOrganicClient }
    const providerFlags = getSettingsProviderFlags(pathname)
    const requiresClient = settingsRouteRequiresClient(pathname)
    const isAccessCheckReady = !requiresClient || client !== null
    const canAccess = canAccessSettingsRoute(pathname, access)

    useEffect(() => {
        if (!isAccessCheckReady || canAccess) {
            return
        }

        router.replace(getSettingsAccessDeniedRedirect(pathname))
    }, [canAccess, isAccessCheckReady, pathname, router])

    if (!isAccessCheckReady || !canAccess) {
        return null
    }

    return (
        <SettingsProvider>
            <RouteSettingsProviders pathname={pathname} {...providerFlags}>
                <SettingsShell>{children}</SettingsShell>
            </RouteSettingsProviders>
        </SettingsProvider>
    )
}
